import { useState } from 'react';

import { AlertCircleIcon, lineupBackground } from '@/common/assets';
import { PlayerOnPitchStats } from '@/common/constants';
import { useNewTranslations } from '@/common/translations';
import type { PlayerOnPitchStatsType, Option } from '@/common/types';
import { T } from '@/common/enums';
import { Filter, FilterChipGroup, PlayerOnPitch, Section } from '@/ui/components';

import { Lineup as LineupComponent } from '../../../components';
import { ApiWidgetType, StatsTypes, TeamFilterOptionValues } from '../../../constants';
import type { TeamFilterOption } from '../../../types';
import { useStatsData } from '../../../hooks';

const Lineup = () => {
  const translate = useNewTranslations();
  const { teams, widget } = useStatsData({
    widgetType: StatsTypes.LINEUP,
  });

  const [teamFilterValue, setTeamFilterValue] = useState<TeamFilterOption>(
    TeamFilterOptionValues.HOME_TEAM,
  );
  const [playerStatsFilterValue, setPlayerStatsFilterValue] =
    useState<PlayerOnPitchStatsType | null>(PlayerOnPitchStats.GOALS);

  if (!widget || widget.type !== ApiWidgetType.LINEUP || !widget?.data) {
    return null;
  }

  const team =
    teamFilterValue === TeamFilterOptionValues.HOME_TEAM ? widget.data.home : widget.data.away;

  const jerseySrc =
    teamFilterValue === TeamFilterOptionValues.HOME_TEAM ? teams.home.jersey : teams.away.jersey;

  const teamFilterOptions: Option<TeamFilterOption>[] = [
    { label: teams.home.name, value: TeamFilterOptionValues.HOME_TEAM },
    { label: teams.away.name, value: TeamFilterOptionValues.AWAY_TEAM },
  ];

  const playerStatsFilterOptions: Option<PlayerOnPitchStatsType>[] = [
    { label: translate('PreMatchStats.Goalscorer'), value: PlayerOnPitchStats.GOALS },
    { label: translate('PreMatchStats.Assists'), value: PlayerOnPitchStats.ASSISTS },
    { label: translate('PreMatchStats.Goal&Assist'), value: PlayerOnPitchStats.BOTH },
  ];

  // T.DARK forces dark mode in the nested components.
  return (
    <Section
      name={translate('PreMatchStats.TabLineup')}
      contentClassName={`${T.DARK} relative p-0 flex flex-col gap-6 justify-between h-full isolate overflow-x-hidden [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]`}
    >
      <div
        className="absolute inset-0 z-[-1]"
        style={{ background: `url(${lineupBackground}) center center / cover no-repeat` }}
      />
      <div className="flex flex-col gap-2 pt-6 px-[12px]">
        <div className="flex items-center self-center gap-1 text-attention">
          <AlertCircleIcon />
          <span className="font-unibet font-medium text-[15px] leading-[9px]">
            {translate('PreMatchStats.PredictedLineUps')}
          </span>
        </div>
        <Filter
          variant="outlined"
          options={teamFilterOptions}
          value={teamFilterValue}
          onValueChange={setTeamFilterValue}
        />
        <FilterChipGroup
          className="mx-[-12px] px-[12px]"
          options={playerStatsFilterOptions}
          value={playerStatsFilterValue}
          onValueChange={setPlayerStatsFilterValue}
        />
      </div>
      <div className="flex flex-col items-center">
        <LineupComponent
          lines={team.lines}
          jerseySrc={jerseySrc}
          enabledStats={playerStatsFilterValue ?? 'none'}
        />
        <div className="w-full h-[11px] bg-lineup-bottom-gradient sm400:opacity-0" />
        <div className="w-full h-px bg-pmw-lineup-gradient-border sm400:opacity-0" />
      </div>
      <div className="p-2">
        <Section>
          <div className="flex flex-col gap-4">
            <span className="font-robotoMedium font-medium text-[12px] leading-[16px] text-pmw-label-primary-dark">
              {translate('PreMatchStats.Substitutes')}
            </span>
            {/*
              overflow-y-hidden is added just in case because sometimes the area is
              scrollable vertically even though it shouldn't
              see https://bitbucket.org/suzu_kindred/stats_widgets/pull-requests/209#comment-781791960
            */}
            <div className="mx-[-8px] px-[8px] flex overflow-y-hidden overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {team.bench.map(({ name, goals, assists }) => (
                <PlayerOnPitch
                  key={`${name}-${goals}-${assists}`}
                  jerseySrc={jerseySrc}
                  playerName={name}
                  goals={goals}
                  assists={assists}
                  activeStat={playerStatsFilterValue ?? undefined}
                />
              ))}
            </div>
          </div>
        </Section>
      </div>
    </Section>
  );
};

export default Lineup;
